import { useUpdateApPayeeInfo } from '@/context/services/ap_payee/UpdateApPayeeInfo.service';
import {
  ForbiddenCodeEnum,
  isAuthError,
  isBadRequestError,
  isForbiddenError,
  isInvalidError,
  isPaymentRequiredError,
} from '@/context/services/type';
import {
  PAYEE_LIST_ERROR_TOAST_MESSAGE_KEY,
  PAYEE_LIST_SUCCESS_TOAST_MESSAGE_KEY,
} from '@/features/ap_payees/components/PayeeDetailDrawer/constants';
import { usePayeeDetailDrawer } from '@/features/ap_payees/components/PayeeDetailDrawer/context/PayeeDetailDrawerProvider';
import { SECTION_KEYS } from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback, useState } from 'react';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import { transformPayeeFormData } from '../helpers/transformPayeeFormData';
import {
  getErrorMessage,
  navigateToListWithMessage,
  navigateToPayeeList,
} from '../utils';
import { usePublishEditPayeeSuccessEvent } from './usePublishEditPayeeSuccessEvent';

type UseSubmitPayeeInfoParams = {
  apPayeeId: string;
};

export const useSubmitPayeeInfo = <TFormData extends FieldValues>({
  apPayeeId,
}: UseSubmitPayeeInfoParams) => {
  const { t } = useTranslation();
  const notify = useToastAPI();
  const publishEditPayeeSuccessEvent = usePublishEditPayeeSuccessEvent();
  const { refetch, setIsEditingSection, onClose } = usePayeeDetailDrawer();
  const [isLoading, setIsLoading] = useState(false);
  const { mutateAsync } = useUpdateApPayeeInfo();

  const handleCloseOnSubmit = useCallback(() => {
    onClose?.(true);
  }, [onClose]);

  const onSubmit: SubmitHandler<TFormData> = useCallback(
    async (formData) => {
      setIsLoading(true);
      try {
        const transformedData = transformPayeeFormData(formData);

        const resp = await mutateAsync({
          id: apPayeeId,
          data: {
            ap_payee: transformedData,
          },
        });

        if (resp.status === 'success') {
          if (resp.data.is_editable) {
            publishEditPayeeSuccessEvent();
            await refetch();
            setIsEditingSection(SECTION_KEYS.NULL);
            notify.success({
              title: t('ap_payees_detail_drawer.update.success'),
              duration: 5,
            });
          } else {
            navigateToListWithMessage(
              t('ap_payees_detail_drawer.update.success'),
              PAYEE_LIST_SUCCESS_TOAST_MESSAGE_KEY,
              handleCloseOnSubmit
            );
          }

          return;
        }

        if (resp.status === 'error') {
          const { statusCode, errors } = resp ?? {};
          const errorMessage = getErrorMessage({
            t,
            statusCode,
            errorMessages: errors?.messages,
            defaultKey: 'ap_payees_detail_drawer.update.failed',
          });

          if (
            isBadRequestError(statusCode) ||
            isInvalidError(statusCode) ||
            isPaymentRequiredError(statusCode)
          ) {
            const isSettingDisabledError =
              errors?.code === 'ai_agent_service_setting_disabled' ||
              errors?.code === 'reconciliation_setting_disabled';

            if (isSettingDisabledError) {
              await refetch();
              setIsEditingSection(SECTION_KEYS.NULL);
            }
            notify.error({
              title: isSettingDisabledError ? errors?.messages : errorMessage,
              duration: 5,
            });
            return;
          }

          if (
            isForbiddenError(statusCode) &&
            errors?.code === ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE
          ) {
            notify.error({
              title: errorMessage,
              duration: 5,
            });
            return;
          }

          if (
            isAuthError(statusCode) ||
            (isForbiddenError(statusCode) && errors?.code === 'no_role')
          ) {
            navigateToPayeeList(handleCloseOnSubmit);
            return;
          }

          navigateToListWithMessage(
            errorMessage,
            PAYEE_LIST_ERROR_TOAST_MESSAGE_KEY,
            handleCloseOnSubmit
          );
          return;
        }

        notify.error({
          title: t('system_error'),
          duration: 5,
        });
      } catch (error) {
        notify.error({
          title: error instanceof Error ? error.message : t('system_error'),
          duration: 5,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      apPayeeId,
      mutateAsync,
      notify,
      refetch,
      setIsEditingSection,
      t,
      handleCloseOnSubmit,
      publishEditPayeeSuccessEvent,
    ]
  );

  return { onSubmit, isLoading };
};
